/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIsomorphicLayoutEffect } from 'hooks/useIsomorphicLayoutEffect';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './InvestmentScreeningWorkflow.module.scss';

const gbp = (n: number) => `£${Math.round(n).toLocaleString('en-GB')}`;
const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const tween = (
  from: number,
  to: number,
  ms: number,
  onUpdate: (value: number) => void,
  rafBucket: number[],
  onDone?: () => void,
) => {
  const t0 = performance.now();

  const frame = (now: number) => {
    const p = Math.min(1, (now - t0) / ms);
    const eased = 1 - (1 - p) ** 3;
    onUpdate(from + (to - from) * eased);

    if (p < 1) {
      rafBucket.push(requestAnimationFrame(frame));
    } else if (onDone) {
      onDone();
    }
  };

  rafBucket.push(requestAnimationFrame(frame));
};

const CardChrome = ({
  name,
  meta,
  children,
  foot,
}: {
  name: string;
  meta: string;
  children: React.ReactNode;
  foot: React.ReactNode;
}) => (
  <div className={styles.card}>
    <div className={styles.cardBar}>
      <span className={styles.cardDot} />
      <span className={styles.cardName}>{name}</span>
      <span className={styles.cardMeta}>{meta}</span>
    </div>
    <div className={styles.cardBody}>{children}</div>
    <div className={styles.cardFoot}>{foot}</div>
  </div>
);

const ScreenBuilderCard = ({ active }: { active: boolean }) => {
  const [max, setMax] = useState(60000000);
  const [count, setCount] = useState(41);
  const [editing, setEditing] = useState(false);
  const [delta, setDelta] = useState<string | null>(null);
  const [barWidth, setBarWidth] = useState(100);

  useEffect(() => {
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    const rafs: number[] = [];
    const clear = () => {
      timeouts.forEach(clearTimeout);
      rafs.forEach(cancelAnimationFrame);
    };

    if (prefersReducedMotion() || !active) {
      clear();
      setEditing(false);
      setDelta(null);
      setMax(60000000);
      setCount(41);
      setBarWidth(100);
      return clear;
    }

    const loop = () => {
      timeouts.push(
        setTimeout(() => {
          setEditing(true);
          tween(60000000, 45000000, 700, setMax, rafs);
        }, 1600),
      );
      timeouts.push(
        setTimeout(() => {
          tween(41, 26, 620, (v) => setCount(Math.round(v)), rafs);
          setBarWidth(63);
        }, 2200),
      );
      timeouts.push(setTimeout(() => setDelta('− 15'), 2850));
      timeouts.push(
        setTimeout(() => {
          setDelta(null);
          tween(45000000, 60000000, 620, setMax, rafs);
          tween(26, 41, 620, (v) => setCount(Math.round(v)), rafs);
          setBarWidth(100);
        }, 6000),
      );
      timeouts.push(setTimeout(() => setEditing(false), 6800));
      timeouts.push(setTimeout(loop, 9600));
    };

    loop();
    return clear;
  }, [active]);

  return (
    <CardChrome
      name="Nordic industrials"
      meta="Saved screen · re-runs daily"
      foot={
        <>
          <div className={styles.trackWrap}>
            <i style={{ width: `${barWidth}%` }} />
          </div>
          <div className={styles.footStat}>
            {delta && <span className={styles.footDelta}>{delta}</span>}
            <b>{count}</b> entities
          </div>
        </>
      }
    >
      <div className={cn(styles.fieldCols)}>
        <span>Field</span>
        <span>Operator</span>
        <span>Value</span>
      </div>
      <div className={styles.fieldRow}>
        <span className={styles.k}>Jurisdiction</span>
        <span className={styles.op}>is</span>
        <span className={styles.v}>GB · IE · SE · NO · DK · FI</span>
      </div>
      <div className={styles.fieldRow}>
        <span className={styles.k}>Activity</span>
        <span className={styles.op}>is</span>
        <span className={styles.v}>SIC 2562 · 2599 · 2811</span>
      </div>
      <div className={cn(styles.fieldRow, editing && styles.fieldRowEdit)}>
        <span className={styles.k}>Turnover</span>
        <span className={styles.op}>between</span>
        <span className={styles.v}>
          £20,000,000 – <b>{gbp(max)}</b>
        </span>
      </div>
      <div className={styles.fieldRow}>
        <span className={styles.k}>EBITDA, derived</span>
        <span className={styles.op}>between</span>
        <span className={styles.v}>£3,000,000 – £10,000,000</span>
      </div>
      <div className={styles.fieldRow}>
        <span className={styles.k}>Accounts filed</span>
        <span className={styles.op}>within</span>
        <span className={styles.v}>12 months</span>
      </div>
    </CardChrome>
  );
};

const RESOLUTION_ROWS = [
  { str: '"Kestrel Precision"', score: 0.97 },
  { str: '"KESTREL PRECISION HLDGS LTD"', score: 0.99 },
  { str: '"Kestrel Prec. Holdings (UK)"', score: 0.96 },
  { str: 'VAT GB 421 8890 06', score: 1.0 },
];

type RowState = 'pending' | 'scanning' | 'matched';

const EntityResolutionCard = ({ active }: { active: boolean }) => {
  const [rowStates, setRowStates] = useState<RowState[]>(RESOLUTION_ROWS.map(() => 'pending'));
  const [liveScores, setLiveScores] = useState<number[]>(RESOLUTION_ROWS.map(() => 0));
  const [done, setDone] = useState(0);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    const rafs: number[] = [];
    const clear = () => {
      timeouts.forEach(clearTimeout);
      rafs.forEach(cancelAnimationFrame);
    };

    if (prefersReducedMotion() || !active) {
      clear();
      setRowStates(RESOLUTION_ROWS.map(() => 'matched'));
      setDone(RESOLUTION_ROWS.length);
      setResolved(true);
      return clear;
    }

    const loop = () => {
      setRowStates(RESOLUTION_ROWS.map(() => 'pending'));
      setDone(0);
      setResolved(false);

      let t = 500;
      RESOLUTION_ROWS.forEach((row, i) => {
        timeouts.push(
          setTimeout(() => {
            setRowStates((prev) => {
              const next = [...prev];
              next[i] = 'scanning';
              return next;
            });
            tween(0, row.score, 480, (v) => setLiveScores((prev) => Object.assign([...prev], { [i]: v })), rafs);
          }, t),
        );
        timeouts.push(
          setTimeout(() => {
            setRowStates((prev) => {
              const next = [...prev];
              next[i] = 'matched';
              return next;
            });
            setDone(i + 1);
          }, t + 560),
        );
        t += 640;
      });

      timeouts.push(setTimeout(() => setResolved(true), t + 300));
      timeouts.push(setTimeout(loop, t + 4200));
    };

    loop();
    return clear;
  }, [active]);

  return (
    <CardChrome
      name="Entity resolution"
      meta="Sample record"
      foot={
        <>
          <div className={styles.trackWrap}>
            <i style={{ width: `${(done / RESOLUTION_ROWS.length) * 100}%` }} />
          </div>
          <div className={styles.footStat}>
            <b>{done}</b> of {RESOLUTION_ROWS.length} resolved
          </div>
        </>
      }
    >
      <div className={styles.entCols}>
        <span>Input string</span>
        <span>Match</span>
      </div>
      {RESOLUTION_ROWS.map((row, i) => (
        <div key={row.str} className={cn(styles.entRow, rowStates[i] === 'scanning' && styles.entRowScan)}>
          <span className={styles.entStr}>{row.str}</span>
          <span className={cn(styles.entMatch, rowStates[i] === 'matched' && styles.entMatchHit)}>
            {rowStates[i] === 'pending' && 'pending'}
            {rowStates[i] === 'scanning' && `matching · ${liveScores[i].toFixed(2)}`}
            {rowStates[i] === 'matched' && `matched · ${row.score.toFixed(2)}`}
          </span>
        </div>
      ))}
      <div className={cn(styles.entOut, resolved && styles.entOutIn)}>
        <div className={styles.entOutHead}>Resolved entity</div>
        <div className={styles.entName}>Kestrel Precision Holdings Limited</div>
        <div className={styles.entId}>
          <span>Company number</span>
          <span className={styles.mono}>08812440</span>
        </div>
        <div className={styles.entId}>
          <span>VAT number</span>
          <span className={styles.mono}>GB 421 8890 06</span>
        </div>
        <div className={styles.entId}>
          <span>Registry</span>
          <span className={styles.mono}>Companies House, GB</span>
        </div>
      </div>
    </CardChrome>
  );
};

const FIN_BARS = [
  { label: 'FY2023', value: '£29.9M', h: 72 },
  { label: 'FY2024', value: '£36.2M', h: 87 },
  { label: 'FY2025', value: '£41.8M', h: 100 },
];

const FIN_ROWS = [
  { label: 'Turnover', values: ['£29,900,000', '£36,200,000', '£41,800,000'] },
  { label: 'Operating profit', values: ['£3,900,000', '£4,800,000', '£5,910,000'] },
  { label: 'Net assets', values: ['£12,400,000', '£15,200,000', '£18,300,000'] },
  { label: 'Employees, estimated', values: ['161', '188', '214'] },
];

const FiledAccountsCard = ({ active }: { active: boolean }) => {
  const [heights, setHeights] = useState<number[]>(FIN_BARS.map(() => 0));
  const [yoy, setYoy] = useState(0);

  useEffect(() => {
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    const rafs: number[] = [];
    const clear = () => {
      timeouts.forEach(clearTimeout);
      rafs.forEach(cancelAnimationFrame);
    };

    if (prefersReducedMotion() || !active) {
      clear();
      setHeights(FIN_BARS.map((b) => b.h));
      setYoy(15.5);
      return clear;
    }

    const loop = () => {
      setHeights(FIN_BARS.map(() => 0));
      setYoy(0);

      FIN_BARS.forEach((bar, i) => {
        timeouts.push(
          setTimeout(
            () => {
              setHeights((prev) => {
                const next = [...prev];
                next[i] = bar.h;
                return next;
              });
            },
            220 + i * 160,
          ),
        );
      });

      timeouts.push(setTimeout(() => tween(0, 15.5, 700, setYoy, rafs), 780));
      timeouts.push(setTimeout(loop, 6800));
    };

    loop();
    return clear;
  }, [active]);

  return (
    <CardChrome
      name="Filed accounts"
      meta="Kestrel Precision Holdings Ltd · sample"
      foot={
        <>
          <span className={styles.footMuted}>Companies House, GB</span>
          <span className={styles.footMuted}>
            Accounts filed <b>14 Mar 2026</b>
          </span>
        </>
      }
    >
      <div className={styles.finChartHead}>
        <span>Turnover, as filed</span>
        <span className={styles.finYoy}>
          <b>+{yoy.toFixed(1)}%</b> year on year
        </span>
      </div>
      <div className={styles.finBars}>
        {FIN_BARS.map((bar, i) => (
          <div key={bar.label} className={cn(styles.finBar, i === 2 && styles.finBarOn)}>
            <span className={styles.finBarValue}>{bar.value}</span>
            <i style={{ height: `${heights[i]}%` }} />
            <span className={styles.finBarLabel}>{bar.label}</span>
          </div>
        ))}
      </div>
      <div className={styles.finCols}>
        <span />
        <span>FY2023</span>
        <span>FY2024</span>
        <span>FY2025</span>
      </div>
      {FIN_ROWS.map((row) => (
        <div key={row.label} className={styles.finRow}>
          <span className={styles.k}>{row.label}</span>
          {row.values.map((v, i) => (
            <span key={i} className={i === 2 ? styles.finHi : undefined}>
              {v}
            </span>
          ))}
        </div>
      ))}
    </CardChrome>
  );
};

const OWNERSHIP_ROWS = [
  { name: 'Ardnamurchan Industrials Ltd', tag: 'Parent · GB', pct: '62.4%', depth: 1 },
  { name: 'Brekke Kapital AS', tag: 'Ultimate parent · NO', pct: '100%', depth: 2 },
  { name: 'Ingrid Brekke', tag: 'Beneficial owner · NO', pct: '74.0%', depth: 3 },
  { name: 'Kestrel Employee Trust', tag: 'Trust · GB', pct: '21.1%', depth: 1 },
  { name: 'Minority holders, 4 named', tag: 'Registered · GB', pct: '16.5%', depth: 1 },
];

const OwnershipCard = ({ active }: { active: boolean }) => {
  const [shown, setShown] = useState(0);
  const [registries, setRegistries] = useState(0);
  const [effective, setEffective] = useState(0);

  useEffect(() => {
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    const rafs: number[] = [];
    const clear = () => {
      timeouts.forEach(clearTimeout);
      rafs.forEach(cancelAnimationFrame);
    };

    if (prefersReducedMotion() || !active) {
      clear();
      setShown(OWNERSHIP_ROWS.length);
      setRegistries(3);
      setEffective(46.2);
      return clear;
    }

    const loop = () => {
      setShown(0);
      setRegistries(0);
      setEffective(0);

      OWNERSHIP_ROWS.forEach((_, i) => {
        timeouts.push(setTimeout(() => setShown(i + 1), 500 + i * 420));
      });

      timeouts.push(setTimeout(() => tween(0, 3, 500, (v) => setRegistries(Math.round(v)), rafs), 2500));
      timeouts.push(setTimeout(() => tween(0, 46.2, 600, setEffective, rafs), 2900));
      timeouts.push(setTimeout(loop, 8200));
    };

    loop();
    return clear;
  }, [active]);

  return (
    <CardChrome
      name="Control chain"
      meta="Sample record · 3 registries"
      foot={
        <>
          <span className={styles.footMuted}>
            Traversed <b>{registries}</b> of 3 registries
          </span>
          <span className={styles.footMuted}>
            Effective interest <b className={styles.ownEffValue}>{effective.toFixed(1)}%</b>
          </span>
        </>
      }
    >
      <div className={styles.ownCols}>
        <span>Entity</span>
        <span>Holding</span>
      </div>
      <div className={cn(styles.ownRow, styles.ownRowRoot)}>
        <span className={styles.ownName}>
          Kestrel Precision Holdings Limited
          <em>Target · GB</em>
        </span>
        <span className={styles.ownPct}>08812440</span>
      </div>
      {OWNERSHIP_ROWS.map((row, i) => (
        <div
          key={row.name}
          className={cn(styles.ownRow, styles[`ownDepth${row.depth}` as 'ownDepth1'], i < shown && styles.ownRowOn)}
        >
          <span className={styles.ownLink} />
          <span className={styles.ownName}>
            {row.name}
            <em>{row.tag}</em>
          </span>
          <span className={styles.ownPct}>{row.pct}</span>
        </div>
      ))}
    </CardChrome>
  );
};

const DEAL_EVENTS = [
  { date: 'Feb 2016', label: 'Incorporation', detail: 'holding structure', value: '—', x: 1 },
  { date: 'Jun 2018', label: 'Financing', detail: 'secured growth facility', value: '£6.0M', x: 24 },
  { date: 'Jan 2021', label: 'Disposal', detail: 'Kestrel Coatings division', value: 'n/d', x: 50 },
  { date: 'Aug 2022', label: 'Acquisition', detail: 'Meridian Fixtures Ltd', value: '100%', x: 66 },
  { date: 'Mar 2024', label: 'Change of control', detail: 'Brekke Kapital AS', value: '62.4%', x: 82 },
  { date: 'Nov 2025', label: 'Acquisition', detail: 'Halden Tooling AS', value: 'n/d', x: 99 },
];

const DealHistoryCard = ({ active }: { active: boolean }) => {
  const [fill, setFill] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    const rafs: number[] = [];
    const clear = () => {
      timeouts.forEach(clearTimeout);
      rafs.forEach(cancelAnimationFrame);
    };

    if (prefersReducedMotion() || !active) {
      clear();
      setFill(100);
      setCount(DEAL_EVENTS.length);
      return clear;
    }

    const loop = () => {
      setFill(0);
      setCount(0);
      tween(0, 100, 2600, setFill, rafs);

      DEAL_EVENTS.forEach((ev, i) => {
        timeouts.push(setTimeout(() => setCount(i + 1), (ev.x / 100) * 2600 + 80));
      });

      timeouts.push(setTimeout(loop, 6600));
    };

    loop();
    return clear;
  }, [active]);

  return (
    <CardChrome
      name="Group deal history"
      meta="Sample record · 3 jurisdictions"
      foot={
        <div className={styles.evFoot}>
          <div className={styles.evLine}>
            <span className={styles.evTrack} />
            <span className={styles.evFill} style={{ width: `${fill}%` }} />
            {DEAL_EVENTS.map((ev) => (
              <i key={ev.date} className={cn(fill >= ev.x && styles.evDotHit)} style={{ left: `${ev.x}%` }} />
            ))}
          </div>
          <div className={styles.evAxis}>
            <span>2016</span>
            <span className={styles.evCount}>
              <b>{count}</b> events
            </span>
            <span>2025</span>
          </div>
        </div>
      }
    >
      <div className={styles.evCols}>
        <span>Date</span>
        <span>Transaction</span>
        <span>Size</span>
      </div>
      {[...DEAL_EVENTS].reverse().map((ev) => (
        <div key={ev.date} className={cn(styles.evRow, fill >= ev.x && styles.evRowPast)}>
          <span className={styles.evDate}>{ev.date}</span>
          <span className={styles.evLabel}>
            <em>{ev.label} ·</em> {ev.detail}
          </span>
          <span className={styles.evValue}>{ev.value}</span>
        </div>
      ))}
    </CardChrome>
  );
};

const SCREEN_CHECKS = [
  { label: 'Sanctions', sub: 'OFAC · EU · UK OFSI · UN', n: 3, result: 'No match', cls: 'ok' },
  { label: 'PEP', sub: 'Officers and shareholders', n: 6, result: 'No match', cls: 'ok' },
  { label: 'Court judgments', sub: 'Credit risk record', n: 9, result: '0 · none registered', cls: 'ok' },
  { label: 'Mortgages', sub: 'Registered against assets', n: 12, result: '2 · registered', cls: 'warn' },
  { label: 'Payment experience', sub: 'Reported behaviour', n: 14, result: 'Pays on time', cls: 'ok' },
] as const;

const ScreeningCard = ({ active }: { active: boolean }) => {
  const [subjects, setSubjects] = useState(0);
  const [doneCount, setDoneCount] = useState(0);

  useEffect(() => {
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    const rafs: number[] = [];
    const clear = () => {
      timeouts.forEach(clearTimeout);
      rafs.forEach(cancelAnimationFrame);
    };

    if (prefersReducedMotion() || !active) {
      clear();
      setSubjects(14);
      setDoneCount(SCREEN_CHECKS.length);
      return clear;
    }

    const loop = () => {
      setSubjects(0);
      setDoneCount(0);

      SCREEN_CHECKS.forEach((check, i) => {
        timeouts.push(
          setTimeout(
            () => {
              tween(SCREEN_CHECKS[i - 1]?.n ?? 0, check.n, 380, (v) => setSubjects(Math.round(v)), rafs);
              setDoneCount(i + 1);
            },
            500 + i * 560,
          ),
        );
      });

      timeouts.push(setTimeout(loop, 500 + SCREEN_CHECKS.length * 560 + 4000));
    };

    loop();
    return clear;
  }, [active]);

  return (
    <CardChrome
      name="Group screening"
      meta="6 entities · sample"
      foot={
        <>
          <span className={cn(styles.sgSum, doneCount >= SCREEN_CHECKS.length && styles.sgSumDone)}>
            {doneCount >= SCREEN_CHECKS.length ? 'Complete' : 'Running'}
          </span>
          <span className={styles.footMuted}>11 officers · 3 shareholders</span>
        </>
      }
    >
      <div className={styles.sgProg}>
        <span className={styles.footMuted}>Screening subjects</span>
        <div className={styles.trackWrap}>
          <i style={{ width: `${(subjects / 14) * 100}%` }} />
        </div>
        <span className={styles.sgN}>
          <b>{subjects}</b> of 14
        </span>
      </div>
      <div className={styles.sgCols}>
        <span>Check</span>
        <span>Result</span>
      </div>
      {SCREEN_CHECKS.map((check, i) => (
        <div
          key={check.label}
          className={cn(
            styles.sgRow,
            i < doneCount && styles.sgRowDone,
            i < doneCount && check.cls === 'ok' && styles.sgRowOk,
            i < doneCount && check.cls === 'warn' && styles.sgRowWarn,
          )}
        >
          <span className={styles.k}>
            {check.label}
            <em>{check.sub}</em>
          </span>
          <span className={styles.v}>{i < doneCount ? check.result : 'screening'}</span>
        </div>
      ))}
    </CardChrome>
  );
};

const STEPS = [
  {
    title: 'Define the universe',
    desc: 'Screen on the fields that are actually filed: registered activity, turnover band, asset base, headcount, incorporation year, filing recency, legal form, registered address. Save the screen and it re-runs itself as registries publish.',
    Card: ScreenBuilderCard,
  },
  {
    title: 'Resolve the entity',
    desc: 'One company arrives as six spellings, three trading names and a group abbreviation. Registration number, VAT number and the Global Database ID pin every record to a single legal entity, so a longlist of 400 is 400 companies rather than 260 counted twice.',
    Card: EntityResolutionCard,
  },
  {
    title: 'Read the filed accounts',
    desc: 'Turnover, gross profit, operating profit, total assets, net assets, cash, trade creditors and estimated headcount, taken from the accounts as the company filed them. Reported in the filing currency, with the latest accounts date filed and the reporting period attached to every line.',
    Card: FiledAccountsCard,
  },
  {
    title: 'Map ownership and control',
    desc: 'Shareholders with percentage holdings and share classes, immediate parent, ultimate parent, subsidiaries by country, and beneficial owners wherever the register publishes them. The chain is built from filings in each jurisdiction rather than inferred from one.',
    Card: OwnershipCard,
  },
  {
    title: 'Trace the deal history',
    desc: 'Prior acquisitions, disposals, funding events and majority changes across the group, each tied to the filing that evidences it. A target that has bought four bolt-ons in six years is a different conversation from one that has never integrated anything.',
    Card: DealHistoryCard,
  },
  {
    title: 'Screen the people',
    desc: 'Directors and their other appointments, roles, appointment and resignation dates, and the PEP and sanctions flag, run across the officers and shareholders of every entity in the group rather than the target alone.',
    Card: ScreeningCard,
  },
];

const STEP_VH = 85;

export const InvestmentScreeningWorkflow = () => {
  const { t } = useInt();
  const panelsRef = useRef<HTMLDivElement>(null);
  const railRefs = useRef<(HTMLDivElement | null)[]>([]);
  const panelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const rafRef = useRef<number>(undefined);

  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0);
  const [pillRect, setPillRect] = useState({ top: 0, height: 0 });
  const [stickHeight, setStickHeight] = useState<number>();

  useIsomorphicLayoutEffect(() => {
    const el = railRefs.current[active];

    if (!el) {
      return;
    }

    setPillRect({ top: el.offsetTop, height: el.offsetHeight });
  }, [active]);

  useIsomorphicLayoutEffect(() => {
    const els = panelRefs.current.filter((el): el is HTMLDivElement => Boolean(el));

    if (!els.length) {
      return undefined;
    }

    const recompute = () => {
      if (window.innerWidth <= 1100) {
        setStickHeight(undefined);
        return;
      }

      setStickHeight(Math.max(...els.map((el) => el.scrollHeight)));
    };

    recompute();

    const resizeObserver = new ResizeObserver(recompute);
    els.forEach((el) => resizeObserver.observe(el));
    window.addEventListener('resize', recompute);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', recompute);
    };
  }, []);

  useEffect(() => {
    const reduced = prefersReducedMotion();

    if (reduced) {
      setActive(0);
      return undefined;
    }

    const poll = () => {
      const node = panelsRef.current;

      if (node && window.innerWidth > 1100) {
        const r = node.getBoundingClientRect();
        const total = r.height - window.innerHeight * 0.7;
        const scrolled = -r.top + window.innerHeight * 0.15;
        const p = Math.max(0, Math.min(1, scrolled / Math.max(1, total)));
        const idx = Math.min(STEPS.length - 1, Math.floor(p * STEPS.length));

        setActive(idx);
        setProgress(p * 100);
      }

      rafRef.current = requestAnimationFrame(poll);
    };

    rafRef.current = requestAnimationFrame(poll);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  const goToStep = (i: number) => {
    const node = panelsRef.current;

    if (!node) {
      return;
    }

    const top = window.scrollY + node.getBoundingClientRect().top;
    const seg = (node.offsetHeight - window.innerHeight * 0.7) / STEPS.length;

    window.scrollTo({ top: top + seg * i + 10, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  };

  return (
    <Section background="#DCE2F5" className={styles.root}>
      <div className={styles.head}>
        <TitleBlock
          align="left"
          title={t('From 600 million to a shortlist')}
          description={t(
            'Six passes over the same primary record. Each one narrows the list and adds evidence to the names that survive.',
          )}
        />
      </div>

      <div className={styles.scrolly}>
        <div className={styles.rail}>
          <span
            className={styles.railPill}
            style={{ transform: `translateY(${pillRect.top}px)`, height: pillRect.height }}
          />
          {STEPS.map((step, i) => (
            <div
              key={step.title}
              ref={(el) => {
                railRefs.current[i] = el;
              }}
              className={cn(styles.railItem, i === active && styles.railItemOn)}
              onClick={() => goToStep(i)}
              role="button"
              tabIndex={0}
            >
              <span className={styles.railNum}>{String(i + 1).padStart(2, '0')}</span>
              <span className={styles.railTitle}>{t(step.title)}</span>
            </div>
          ))}
          <div className={styles.railProg}>
            <i style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div
          className={styles.panels}
          ref={panelsRef}
          style={{
            minHeight: stickHeight
              ? `calc(${STEPS.length * STEP_VH}vh + ${stickHeight}px)`
              : `${STEPS.length * STEP_VH}vh`,
          }}
        >
          <div className={styles.panelStick} style={stickHeight ? { height: `${stickHeight}px` } : undefined}>
            {STEPS.map((step, i) => {
              const Card = step.Card;

              return (
                <div key={step.title} className={cn(styles.panel, i === active && styles.panelOn)}>
                  <div
                    ref={(el) => {
                      panelRefs.current[i] = el;
                    }}
                    className={styles.panelInner}
                  >
                    <h3 className={styles.panelTitle}>{t(step.title)}</h3>
                    <p className={styles.panelDesc}>{t(step.desc)}</p>
                    <Card active={i === active} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Section>
  );
};
